import { Suspense, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Role } from '@keystone/shared';
import { Avatar, Button, LinkButton, PageFallback, RoleBadge, Select } from './ui.js';
import { useAuth } from '../state/auth.js';
import { useToast } from '../state/toast.js';
import { cyclePreference, useThemePreference } from '../lib/theme.js';
import { useEscape } from '../lib/hooks.js';

/**
 * The application shell.
 *
 * ## Navigation, and what "current" means
 *
 * Every link is a `NavLink`, which sets `aria-current="page"` on the active one.
 * That attribute is what a screen reader announces as "current page"; styling the
 * active link with a background colour and nothing else leaves a screen reader
 * user with no idea where they are in a six-item menu.
 *
 * ## Organisation-scoped routes
 *
 * The console's URLs are `#/orgs/<slug>/members` rather than `#/members` with the
 * organisation in state. Two reasons: a link to a specific organisation's audit
 * log is then shareable and bookmarkable, and the browser's back button works
 * across organisation switches instead of silently changing what "Members" means.
 */

interface NavItem {
  to: string;
  label: string;
  /** Permission-free visibility rule: a role that cannot read the page never sees it. */
  visible: (role: Role | null) => boolean;
  end?: boolean;
}

const ORG_NAV: readonly NavItem[] = [
  { to: '', label: 'Overview', visible: () => true, end: true },
  { to: 'members', label: 'Members', visible: () => true },
  {
    to: 'invitations',
    label: 'Invitations',
    visible: (role) => role === 'owner' || role === 'admin',
  },
  {
    to: 'api-keys',
    label: 'API keys',
    visible: (role) => role === 'owner' || role === 'admin',
  },
  {
    to: 'audit',
    label: 'Audit log',
    visible: (role) => role === 'owner' || role === 'admin',
  },
  { to: 'settings', label: 'Settings', visible: () => true },
];

export function Layout(): ReactNode {
  const { user, organizations, organizationFor, signOut, signOutEverywhere } = useAuth();
  const { push } = useToast();
  const { slug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const organization = organizationFor(slug);
  const role = organization?.role ?? null;

  // Close the disclosures on navigation. Without this, choosing an organisation
  // from the menu leaves the menu open over the page it just navigated to.
  useEffect(() => {
    setMenuOpen(false);
    setNavOpen(false);
  }, [location.pathname]);

  useEscape(menuOpen, () => setMenuOpen(false));

  // Click-outside. Attached only while the menu is open, so a closed menu costs
  // nothing — and the listener is on `pointerdown` rather than `click` so that a
  // press-and-drag that ends elsewhere does not count as an outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const handleSignOutEverywhere = async (): Promise<void> => {
    const result = await signOutEverywhere();
    if (result.ok) {
      push({
        tone: 'success',
        title: 'Signed out everywhere',
        description: `${result.data} session${result.data === 1 ? '' : 's'} revoked, including this one.`,
      });
      navigate('/login', { replace: true });
    } else {
      push({ tone: 'danger', title: 'Could not sign out', description: result.error.userMessage() });
    }
  };

  return (
    <div className="min-h-dvh bg-canvas">
      {/*
        The first focusable element on the page. A keyboard user who tabs once
        lands here and can jump past a sidebar with twelve links straight to the
        content. Without it they tab through the whole shell on every navigation.
      */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70] focus:rounded-lg focus:bg-brand focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-brand-ink"
      >
        Skip to content
      </a>

      <div className="mx-auto flex max-w-[110rem] flex-col lg:flex-row">
        <Sidebar
          navOpen={navOpen}
          onCloseNav={() => setNavOpen(false)}
          role={role}
          slug={slug}
          organizations={organizations}
          onSwitch={(next) => navigate(`/orgs/${next.slug}`)}
          currentSlug={slug}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-line bg-surface/85 px-4 py-2.5 backdrop-blur-sm sm:px-6">
            <button
              type="button"
              onClick={() => setNavOpen((open) => !open)}
              aria-expanded={navOpen}
              aria-controls="sidebar"
              aria-label="Toggle navigation"
              className="rounded-lg border border-line-strong p-1.5 text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink lg:hidden"
            >
              <MenuIcon />
            </button>

            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-ink">
                {organization ? organization.name : 'Keystone'}
              </p>
              {organization ? (
                <p className="truncate font-mono text-[0.6875rem] text-ink-subtle">
                  {organization.slug}
                </p>
              ) : null}
            </div>

            <ThemeToggle />

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                className="flex items-center gap-2 rounded-lg border border-line-strong py-1 pr-2 pl-1 transition-colors hover:bg-surface-2"
              >
                <Avatar name={user?.name ?? 'Account'} src={user?.avatarUrl ?? null} size="sm" />
                <span className="hidden max-w-[10rem] truncate text-[0.8125rem] text-ink sm:inline">
                  {user?.name ?? 'Account'}
                </span>
              </button>

              {menuOpen ? (
                <div
                  role="menu"
                  aria-label="Account"
                  className="absolute right-0 z-40 mt-2 w-64 rounded-xl border border-line bg-surface p-1.5 shadow-pop"
                >
                  <div className="px-2.5 py-2">
                    <p className="truncate text-[0.8125rem] font-medium text-ink">
                      {user?.name ?? 'Account'}
                    </p>
                    <p className="truncate text-xs text-ink-muted">{user?.email ?? ''}</p>
                  </div>

                  <div className="my-1 border-t border-line" />

                  <MenuItem to="/sessions" label="Active sessions" />
                  <MenuItem to="/try" label="Try it" />

                  <div className="my-1 border-t border-line" />

                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void handleSignOut()}
                    className="w-full rounded-lg px-2.5 py-2 text-left text-[0.8125rem] text-ink transition-colors hover:bg-surface-2"
                  >
                    Sign out of this device
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void handleSignOutEverywhere()}
                    className="w-full rounded-lg px-2.5 py-2 text-left text-[0.8125rem] text-danger transition-colors hover:bg-danger-soft"
                  >
                    Sign out everywhere
                  </button>
                </div>
              ) : null}
            </div>
          </header>

          <main id="main" className="min-w-0 flex-1 px-4 py-5 sm:px-6 sm:py-7">
            {/*
              The inner Suspense boundary. Page chunks are loaded lazily, and
              without this the fallback would be the outer one — which replaces
              the whole shell, so every navigation would blank the sidebar and
              header and then redraw them. Here the shell stays put and only the
              content area shows a skeleton.
            */}
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </main>

          <footer className="border-t border-line px-4 py-4 text-xs text-ink-subtle sm:px-6">
            Keystone — multi-tenant authentication and RBAC on Cloudflare Workers.{' '}
            <a
              className="underline decoration-dotted underline-offset-2 hover:text-ink"
              href="/openapi.json"
              target="_blank"
              rel="noreferrer"
            >
              OpenAPI document
            </a>{' '}
            ·{' '}
            <a
              className="underline decoration-dotted underline-offset-2 hover:text-ink"
              href="/docs"
              target="_blank"
              rel="noreferrer"
            >
              API reference
            </a>
          </footer>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sidebar                                                                   */
/* -------------------------------------------------------------------------- */

function Sidebar({
  navOpen,
  onCloseNav,
  role,
  slug,
  organizations,
  currentSlug,
  onSwitch,
}: {
  navOpen: boolean;
  onCloseNav: () => void;
  role: Role | null;
  slug: string | undefined;
  organizations: { slug: string; name: string; role?: Role }[];
  currentSlug: string | undefined;
  onSwitch: (organization: { slug: string }) => void;
}): ReactNode {
  const visibleNav = ORG_NAV.filter((item) => item.visible(role));

  return (
    <>
      {/* Mobile: the sidebar overlays and the backdrop dismisses it. */}
      {navOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          tabIndex={-1}
          onClick={onCloseNav}
          className="fixed inset-0 z-30 bg-[var(--k-overlay)] lg:hidden"
        />
      ) : null}

      <aside
        id="sidebar"
        className={`${
          navOpen ? 'block' : 'hidden'
        } fixed inset-y-0 left-0 z-40 w-72 overflow-y-auto border-r border-line bg-surface p-3 lg:sticky lg:top-0 lg:block lg:h-dvh lg:w-64 lg:shrink-0`}
      >
        <div className="flex items-center gap-2 px-2 py-2.5">
          <span
            aria-hidden="true"
            className="flex size-7 items-center justify-center rounded-md bg-brand text-sm font-bold text-brand-ink"
          >
            K
          </span>
          <span className="text-sm font-semibold tracking-tight text-ink">Keystone</span>
        </div>

        <div className="mt-3 px-1">
          {organizations.length > 0 ? (
            <label className="block">
              <span className="sr-only">Switch organisation</span>
              <Select
                value={currentSlug ?? ''}
                onChange={(event) => {
                  const next = organizations.find((org) => org.slug === event.target.value);
                  if (next) onSwitch(next);
                }}
                className="text-[0.8125rem]"
              >
                {currentSlug === undefined ? (
                  <option value="" disabled>
                    Select an organisation
                  </option>
                ) : null}
                {organizations.map((org) => (
                  <option key={org.slug} value={org.slug}>
                    {org.name}
                  </option>
                ))}
              </Select>
            </label>
          ) : (
            <p className="px-1 py-1.5 text-xs text-ink-subtle">No organisations yet</p>
          )}
        </div>

        {role ? (
          <div className="mt-2 flex items-center gap-2 px-2">
            <span className="text-[0.6875rem] text-ink-subtle">Your role</span>
            <RoleBadge role={role} />
          </div>
        ) : null}

        <nav aria-label="Organisation" className="mt-4">
          <ul className="flex flex-col gap-0.5">
            {slug
              ? visibleNav.map((item) => (
                  <li key={item.label}>
                    <NavLink
                      to={item.to === '' ? `/orgs/${slug}` : `/orgs/${slug}/${item.to}`}
                      end={item.end}
                      className={({ isActive }) =>
                        `block rounded-lg px-2.5 py-1.5 text-[0.8125rem] transition-colors ${
                          isActive
                            ? 'bg-brand-soft font-medium text-brand'
                            : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))
              : null}
          </ul>
        </nav>

        <nav aria-label="Account" className="mt-4 border-t border-line pt-4">
          <ul className="flex flex-col gap-0.5">
            <li>
              <NavLink
                to="/sessions"
                className={({ isActive }) =>
                  `block rounded-lg px-2.5 py-1.5 text-[0.8125rem] transition-colors ${
                    isActive
                      ? 'bg-brand-soft font-medium text-brand'
                      : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                  }`
                }
              >
                Active sessions
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/try"
                className={({ isActive }) =>
                  `block rounded-lg px-2.5 py-1.5 text-[0.8125rem] transition-colors ${
                    isActive
                      ? 'bg-brand-soft font-medium text-brand'
                      : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                  }`
                }
              >
                Try it
              </NavLink>
            </li>
          </ul>
        </nav>
      </aside>
    </>
  );
}

function MenuItem({ to, label }: { to: string; label: string }): ReactNode {
  return (
    <NavLink
      to={to}
      role="menuitem"
      className="block rounded-lg px-2.5 py-2 text-[0.8125rem] text-ink transition-colors hover:bg-surface-2"
    >
      {label}
    </NavLink>
  );
}

/* -------------------------------------------------------------------------- */
/*  Theme toggle                                                              */
/* -------------------------------------------------------------------------- */

/**
 * A three-state theme control.
 *
 * A single button that cycles light -> dark -> system, with the current state
 * named in its accessible label. The alternative — a switch with two positions —
 * cannot express "follow the operating system", and that is the state most people
 * want.
 */
function ThemeToggle(): ReactNode {
  const preference = useThemePreference();
  const [announced, setAnnounced] = useState<string | null>(null);

  const label =
    preference === 'system' ? 'Theme: match system' : `Theme: ${preference}`;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`${label}. Activate to change.`}
        onClick={() => {
          const next = cyclePreference();
          setAnnounced(next === 'system' ? 'Theme set to match system' : `Theme set to ${next}`);
        }}
      >
        <span aria-hidden="true">{preference === 'light' ? 'Light' : preference === 'dark' ? 'Dark' : 'Auto'}</span>
      </Button>
      {/*
        A live region for the change, because the button's own label changing is
        not reliably announced when the button is not focused.
      */}
      <span aria-live="polite" className="sr-only">
        {announced}
      </span>
    </>
  );
}

function MenuIcon(): ReactNode {
  return (
    <svg viewBox="0 0 20 20" className="size-4" fill="none" aria-hidden="true" focusable="false">
      <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page scaffolding                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The heading block at the top of every page.
 *
 * `level` exists so that a page nested inside another section keeps a valid
 * heading outline — skipping from `h1` to `h3` is a real navigation problem for
 * someone reading by heading, which is a common way to skim a page with a screen
 * reader.
 */
export function PageHeader({
  title,
  description,
  actions,
  badge,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  badge?: ReactNode;
}): ReactNode {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold tracking-tight text-ink">{title}</h1>
          {badge}
        </div>
        {description ? (
          <p className="mt-1 max-w-2xl text-[0.8125rem] text-ink-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** A "nothing here yet" page for a caller with no organisations. */
export function NoOrganizations(): ReactNode {
  return (
    <div className="mx-auto max-w-lg py-12 text-center">
      <h1 className="text-lg font-semibold text-ink">You are not a member of an organisation yet</h1>
      <p className="mt-2 text-[0.8125rem] text-ink-muted">
        Create one to get started, or accept an invitation if someone has sent you one.
      </p>
      <div className="mt-5 flex justify-center gap-2">
        <LinkButton to="/orgs/new" variant="primary">
          Create an organisation
        </LinkButton>
      </div>
    </div>
  );
}

/** A small labelled row, for the settings and overview detail lists. */
export function DetailList({ children }: { children: ReactNode }): ReactNode {
  return <dl className="divide-y divide-line">{children}</dl>;
}

export function DetailRow({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}): ReactNode {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3">
      <dt className="min-w-0 text-[0.8125rem] text-ink">
        {label}
        {hint ? <p className="mt-0.5 text-xs font-normal text-ink-subtle">{hint}</p> : null}
      </dt>
      <dd className="text-[0.8125rem] text-ink-muted">{children}</dd>
    </div>
  );
}
